import type { BaseDevice } from '../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { KeyboardKeysFeature } from '../../../features/keyboard-keys/contract'
import type { platform } from '../../../types/config'
import type {
  Action,
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  Macro,
  wheel,
} from '../../../types/keyboardKeys'
import type { CH315KeyboardProtocol } from '../protocols'
import { CH315Memory } from '../capabilities/memory'
import {
  CH315_FEATURE_LAYOUT,
  type CH315LayerIndex,
  getCH315LayerBase,
} from '../types/feature-types'
import { CH315FeatureMemory, decodeCH315Text, readUint16LE } from './memory'

export class CH315KeyboardKeys extends KeyboardKeysFeature {
  private readonly memory: CH315FeatureMemory
  private readonly writableMemory: CH315Memory

  constructor(device: BaseDevice, protocol: CH315KeyboardProtocol) {
    super(device)
    this.memory = new CH315FeatureMemory(protocol)
    this.writableMemory = new CH315Memory(protocol)
  }

  getLayerKeyCount(): number {
    return CH315_FEATURE_LAYOUT.keyCount
  }

  async getLayerKeysMap(isFn = false, platform: platform = 'win'): Promise<LayerKeysMap> {
    const layer = resolveLayer(isFn, platform)
    const bytes = await this.memory.read(
      getCH315LayerBase(this.device.currentConfig.index, layer) + 4,
      CH315_FEATURE_LAYOUT.keyCount * 4,
    )
    return decodeCH315LayerMap(bytes)
  }

  async *getLayerKeysMapStream(
    isFn = false,
    batchSize = 24,
    platform: platform = 'win',
  ): AsyncIterable<LayerKeysMap> {
    if (!Number.isInteger(batchSize) || batchSize <= 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'batchSize must be a positive integer')
    }
    const all = await this.getLayerKeysMap(isFn, platform)
    for (let offset = 0; offset < all.length; offset += batchSize) {
      yield all.slice(offset, offset + batchSize)
    }
  }

  async getMacroList(): Promise<Macro[]> {
    const layout = this.getMacroLayout()
    const names = await this.memory.read(
      layout.nameBase,
      layout.count * CH315_FEATURE_LAYOUT.macroNameBytes,
    )
    return decodeCH315MacroNames(names, layout.count)
  }

  async *getMacroListStream(batchSize = 16): AsyncIterable<Macro[]> {
    if (!Number.isInteger(batchSize) || batchSize <= 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'batchSize must be a positive integer')
    }
    const all = await this.getMacroList()
    for (let offset = 0; offset < all.length; offset += batchSize) {
      yield all.slice(offset, offset + batchSize)
    }
  }

  async getMacroByIndex(index: number): Promise<Macro> {
    const layout = this.getMacroLayout()
    assertMacroIndex(index, layout.count)
    const [payload, nameBytes] = await Promise.all([
      this.memory.read(
        layout.dataBase + index * CH315_FEATURE_LAYOUT.macroStride,
        CH315_FEATURE_LAYOUT.macroStride,
      ),
      this.memory.read(
        layout.nameBase + index * CH315_FEATURE_LAYOUT.macroNameBytes,
        CH315_FEATURE_LAYOUT.macroNameBytes,
      ),
    ])
    return decodeCH315Macro(payload, decodeCH315Text(nameBytes), index)
  }

  async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    const layout = this.getMacroLayout()
    assertMacroIndex(index, layout.count)
    const payload = encodeCH315FeatureMacro(macro)
    const name = encodeCH315MacroName(macro.name)
    const dataAddress = layout.dataBase + index * CH315_FEATURE_LAYOUT.macroStride
    const nameAddress = layout.nameBase + index * CH315_FEATURE_LAYOUT.macroNameBytes

    await this.writableMemory.writeBulk(dataAddress, payload, {
      finalizeAddress: dataAddress,
      verifyReadback: true,
    })
    await this.writableMemory.writeBulk(nameAddress, name, { verifyReadback: true })
  }

  // Base mapping and extension slots remain read-only until their Checksum32/allocation
  // behavior has been verified on hardware.

  private getMacroLayout(): { dataBase: number; nameBase: number; count: number } {
    const layout = this.device.manifest.driverOptions?.ch315MacroLayout
    if (layout === 'detailed96') {
      return { dataBase: 0x10020000, nameBase: 0x1002c000, count: 96 }
    }
    if (layout === 'legacy128') {
      return { dataBase: 0x10020000, nameBase: 0x10030000, count: 128 }
    }
    throw new DeviceError(
      DeviceErrorCode.NOT_SUPPORTED,
      'CH315 macro layout is ambiguous; set driverOptions.ch315MacroLayout after hardware verification',
    )
  }
}

function resolveLayer(isFn: boolean, platform: platform): CH315LayerIndex {
  if (platform === 'mac') return isFn ? 3 : 2
  return isFn ? 1 : 0
}

export function decodeCH315LayerMap(bytes: Uint8Array): LayerKeysMap {
  if (bytes.byteLength < CH315_FEATURE_LAYOUT.keyCount * 4) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 layer map is truncated')
  }
  return Array.from({ length: CH315_FEATURE_LAYOUT.keyCount }, (_, index): keyMapItem => {
    const raw = bytes.subarray(index * 4, index * 4 + 4)
    return { index, keyAttr: decodeCH315BaseKey(raw) }
  })
}

export function decodeCH315BaseKey(raw: Uint8Array): keyAttr {
  const type = raw[0]
  const result: keyAttr = { type }
  switch (type) {
    case 0x01:
      result.code = [raw[1]]
      break
    case 0x02:
      result.modifyKey = raw[1]
      result.code = [raw[2], raw[3]].filter((value) => value !== 0xff)
      break
    case 0x03:
      result.code = [(raw[1] << 8) | raw[2]]
      break
    case 0x04:
      result.code = [raw[1]]
      break
    case 0x05:
    case 0x06: {
      const parsed: wheel = {
        type: raw[1] >>> 4,
        number: raw[2] > 127 ? raw[2] - 256 : raw[2],
        time: ((raw[1] & 0x0f) << 8) | raw[3],
      }
      if (type === 0x05) result.colWheel = parsed
      else result.rowWheel = parsed
      break
    }
    case 0x07:
      result.macro = { loop: readUint16LE(raw, 1), index: raw[3] }
      break
    case 0x08:
      result.code = [raw[1]]
      if (raw[1] === 0x24) result.confIndex = raw[2]
      break
    default:
      if (type >= 0x09 && type <= 0x10) {
        // 扩展表基址未在当前地址表中给出；保留已明确的槽位引用。
        result.confIndex = raw[1]
      }
  }
  return result
}

export function decodeCH315MacroNames(
  bytes: Uint8Array,
  count: number = CH315_FEATURE_LAYOUT.macroCount,
): Macro[] {
  const macros: Macro[] = []
  for (let index = 0; index < count; index += 1) {
    const start = index * CH315_FEATURE_LAYOUT.macroNameBytes
    const name = decodeCH315Text(bytes.subarray(start, start + CH315_FEATURE_LAYOUT.macroNameBytes))
    if (name) macros.push({ index, name })
  }
  return macros
}

export function decodeCH315Macro(payload: Uint8Array, name: string, index: number): Macro {
  if (payload.byteLength < 4) {
    throw new DeviceError(DeviceErrorCode.READ_FAILED, 'CH315 macro payload is truncated')
  }
  const length = readUint16LE(payload, 0)
  const loop = readUint16LE(payload, 2)
  if (length < 1 || length > 504) return { name, index, loop: 0, package: [] }
  const used = payload.subarray(0, Math.min(length, payload.byteLength))
  const actions = decodeCH315MacroActions(used)
  const ignoreDelay = actions.length > 0 && actions.every((action) => action.time === 2)
  return { name, index, loop, package: actions, ignoreDelay }
}

/** CH315 功能定义规定每个宏动作固定为 2 B 状态 + 1 B 键码。 */
export function decodeCH315MacroActions(payload: Uint8Array): Action[] {
  const actions: Action[] = []
  for (let offset = 4; offset + 2 < payload.byteLength; offset += 3) {
    const status = readUint16LE(payload, offset)
    actions.push({
      type: status & 0x01,
      time: (status >>> 1) & 0x7fff,
      key: payload[offset + 2],
    })
  }
  return actions
}

export function encodeCH315FeatureMacro(macro: Macro): Uint8Array {
  const actions = macro.package ?? []
  const length = 4 + actions.length * 3
  if (length > 504) {
    throw new DeviceError(
      DeviceErrorCode.MAX_LIMIT_REACHED,
      'CH315 macro payload exceeds 504 bytes',
    )
  }
  const loop = macro.loop ?? 0
  if (!Number.isInteger(loop) || loop < 0 || loop > 0xffff) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 macro loop must be 0..65535')
  }
  const result = new Uint8Array(CH315_FEATURE_LAYOUT.macroStride).fill(0xff)
  writeUint16LE(result, 0, length)
  writeUint16LE(result, 2, loop)
  actions.forEach((action, index) => {
    if (!Number.isInteger(action.time) || action.time < 0 || action.time > 0x7fff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 macro delay must be 0..32767 ms')
    }
    if (!Number.isInteger(action.key) || action.key < 0 || action.key > 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 macro key code must be 0..255')
    }
    const offset = 4 + index * 3
    writeUint16LE(result, offset, (action.time << 1) | (action.type === 1 ? 1 : 0))
    result[offset + 2] = action.key
  })
  return result
}

function encodeCH315MacroName(value: string): Uint8Array {
  if (value.length > CH315_FEATURE_LAYOUT.macroNameBytes || !/^[\x20-\x7e]*$/.test(value)) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      'CH315 macro name must contain at most 16 printable ASCII characters',
    )
  }
  const result = new Uint8Array(CH315_FEATURE_LAYOUT.macroNameBytes)
  for (let index = 0; index < value.length; index += 1) result[index] = value.charCodeAt(index)
  return result
}

function writeUint16LE(data: Uint8Array, offset: number, value: number): void {
  data[offset] = value & 0xff
  data[offset + 1] = (value >>> 8) & 0xff
}

function assertMacroIndex(index: number, count: number): void {
  if (!Number.isInteger(index) || index < 0 || index >= count) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `CH315 macro index must be 0..${count - 1}`,
    )
  }
}
